import React from 'react';
import bearing from '@turf/bearing';

import {
    useDebriefDispatch,
    useDebriefRouter,
    useDebriefSelector,
} from 'ff/debrief/hooks';
import {
    extractTrimmedValues,
    findCurrentSegment,
    findNearestPoint,
    trackPointToGeojsonPoint,
} from 'ff/debrief/utils';
import * as chronoStore from 'ff/debrief/features/ChronoSlice';
import * as appStore from 'ff/debrief/features/AppSlice';
import * as api from 'ff/debrief/features/TracklogApi';

export function useChrono() {
    const state = useDebriefSelector(s => s.chronoSlice);
    const dispatch = useDebriefDispatch();
    const { actions: chronoActions } = chronoStore;
    const { actions: appActions } = appStore;

    const {
        routeParams: { tracklogId },
    } = useDebriefRouter();
    const { data: tl } = api.useGetTracklogByIdQuery(tracklogId, {
        skip: !tracklogId,
    });
    const { data: debrief } = api.useGetDebriefByTrackUUIDQuery(tracklogId, {
        skip: !tracklogId,
    });

    const trimmedValues = extractTrimmedValues(tl);
    const currentTime = trimmedValues?.timestampStart + state.currentOffset;
    const points = tl?.points?.data;

    const currentPoint = React.useMemo(() => {
        if (!points) return null;
        const idx = findNearestPoint(points, currentTime);
        const nearestTrackPoint = points[idx];
        const nextTrackPoint = points[idx + 1];
        const prevTrackPoint = points[idx - 1];
        const nearestPoint = trackPointToGeojsonPoint(nearestTrackPoint);
        const nextPoint = trackPointToGeojsonPoint(nextTrackPoint);
        const prevPoint = trackPointToGeojsonPoint(prevTrackPoint);
        const currentBearing = nextTrackPoint
            ? bearing(nearestPoint, nextPoint)
            : bearing(prevPoint, nearestPoint);
        return {
            timestamp: nearestTrackPoint[0],
            latitude: nearestTrackPoint[1],
            longitude: nearestTrackPoint[2],
            groundSpeed: nearestTrackPoint[3],
            altitudeFeet: nearestTrackPoint[4],
            cabinAltitudeFeet: nearestTrackPoint[5],
            bankDeg: nearestTrackPoint[6],
            pitchDeg: nearestTrackPoint[7],
            gLoad: nearestTrackPoint[8],
            trueAirSpeed: nearestTrackPoint[9],
            magneticCourse: nearestTrackPoint[10],
            magneticHeading: nearestTrackPoint[11],
            windDirection: nearestTrackPoint[12],
            windVelocity: nearestTrackPoint[13],
            verticalSpeed: nearestTrackPoint[14],
            turnRate: nearestTrackPoint[15],
            magneticVariance: nearestTrackPoint[16],
            indicatedAirSpeed: nearestTrackPoint[17],
            crosswind: nearestTrackPoint[18],
            headwind: nearestTrackPoint[19],
            aboveGroundLevel: nearestTrackPoint[20],
            bearing: currentBearing,
        };
    }, [currentTime, points]);

    const segments = debrief?.debriefSegments;

    const play = () => {
        dispatch(chronoStore.playTicker(tracklogId));
    };

    const pause = () => {
        dispatch(chronoActions.pauseTicker());
    };

    const setOffset = (offset: number) => {
        const currentSegment = findCurrentSegment(segments, offset);
        dispatch(chronoActions.setOffset(offset));
        dispatch(appActions.setSelectedSegment(currentSegment));
    };

    const setSpeed = (speed: number) => {
        dispatch(chronoStore.setSpeed(speed));
    };

    const resetChrono = () => {
        dispatch(chronoActions.reset());
    };

    return {
        offset: state.currentOffset,
        speed: state.tickerSpeed,
        currentTime: currentTime,
        currentPoint: currentPoint,
        isTicking: Boolean(state.ticker),
        play: play,
        pause: pause,
        setOffset: setOffset,
        setSpeed: setSpeed,
        resetChrono: resetChrono,
    };
}
